import React, { useEffect, useState } from "react";
import { Button, DatePicker as AntdDatePicker, Drawer } from "antd";
import { CalendarOutlined, CloseCircleFilled } from "@ant-design/icons";
import { DatePickerView } from "antd-mobile";
import { useSelector } from "react-redux";
import moment from "moment";
import { formatMomentValue, getPickerMode, normalizeMoment } from "../ResponsivePicker/utils";
import "../ResponsivePicker/style.css";

const getDate = (value) => normalizeMoment(value)?.toDate() || new Date();
const toMoment = (date, picker) => {
  const next = moment(date);
  if (picker === "month") return next.startOf("month");
  if (picker === "year") return next.startOf("year");
  return next;
};

const ResponsiveDatePickerBase = (props) => {
  const {
    value,
    defaultValue,
    onChange,
    format = props.picker === "month" ? "YYYY-MM" : props.picker === "year" ? "YYYY" : "YYYY-MM-DD",
    picker,
    placeholder = "Select date",
    disabled,
    disabledDate,
    allowClear = true,
    className,
    style,
    ...rest
  } = props;
  const isPC = useSelector((state) => state.App?.isPC ?? true);
  const controlled = value !== undefined;
  const [innerValue, setInnerValue] = useState(defaultValue);
  const currentValue = controlled ? value : innerValue;
  const [open, setOpen] = useState(false);
  const [draftDate, setDraftDate] = useState(getDate(currentValue));
  const displayValue = formatMomentValue(currentValue, format);

  useEffect(() => {
    if (open) setDraftDate(getDate(currentValue));
  }, [currentValue, open]);

  if (isPC) {
    return (
      <AntdDatePicker
        {...rest}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        format={format}
        picker={picker}
        placeholder={placeholder}
        disabled={disabled}
        disabledDate={disabledDate}
        allowClear={allowClear}
        className={className}
        style={style}
      />
    );
  }

  const emitChange = (nextMoment) => {
    if (!controlled) setInnerValue(nextMoment);
    onChange?.(nextMoment, nextMoment ? nextMoment.format(format) : "");
  };

  const clearValue = (event) => {
    event.stopPropagation();
    emitChange(null);
  };

  return (
    <>
      <div
        className={`responsive-picker-trigger${disabled ? " responsive-picker-trigger-disabled" : ""} ${className || ""}`}
        style={style}
        onClick={() => !disabled && setOpen(true)}
      >
        <span className={`responsive-picker-trigger-value${displayValue ? "" : " responsive-picker-trigger-placeholder"}`}>
          {displayValue || placeholder}
        </span>
        <span className="responsive-picker-trigger-icons">
          {allowClear && displayValue && !disabled ? <CloseCircleFilled className="responsive-picker-clear" onClick={clearValue} /> : null}
          <CalendarOutlined />
        </span>
      </div>
      <Drawer
        open={open}
        title={placeholder}
        placement="bottom"
        height="64vh"
        className="responsive-picker-drawer"
        onClose={() => setOpen(false)}
        destroyOnClose
      >
        <div className="responsive-picker-body">
          <DatePickerView
            className="responsive-date-view"
            value={draftDate}
            precision={getPickerMode(picker)}
            onChange={setDraftDate}
          />
        </div>
        <div className="responsive-picker-footer">
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            type="primary"
            onClick={() => {
              const next = toMoment(draftDate, picker);
              if (disabledDate?.(next)) return;
              emitChange(next);
              setOpen(false);
            }}
          >
            Confirm
          </Button>
        </div>
      </Drawer>
    </>
  );
};

const ResponsiveRangePicker = (props) => {
  const {
    value,
    defaultValue,
    onChange,
    format = props.picker === "month" ? "YYYY-MM" : "YYYY-MM-DD",
    picker,
    placeholder = ["Start date", "End date"],
    disabled,
    disabledDate,
    allowClear = true,
    className,
    style,
    ...rest
  } = props;
  const isPC = useSelector((state) => state.App?.isPC ?? true);
  const controlled = value !== undefined;
  const [innerValue, setInnerValue] = useState(defaultValue);
  const currentValue = controlled ? value : innerValue;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("start");
  const [draftStart, setDraftStart] = useState(getDate(currentValue?.[0]));
  const [draftEnd, setDraftEnd] = useState(getDate(currentValue?.[1]));
  const startDisplay = formatMomentValue(currentValue?.[0], format);
  const endDisplay = formatMomentValue(currentValue?.[1], format);
  const displayValue = startDisplay && endDisplay ? `${startDisplay} - ${endDisplay}` : "";
  const placeholderText = Array.isArray(placeholder) ? placeholder.join(" - ") : placeholder;

  useEffect(() => {
    if (open) {
      setDraftStart(getDate(currentValue?.[0]));
      setDraftEnd(getDate(currentValue?.[1]));
      setActive("start");
    }
  }, [currentValue, open]);

  if (isPC) {
    return (
      <AntdDatePicker.RangePicker
        {...rest}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        format={format}
        picker={picker}
        placeholder={placeholder}
        disabled={disabled}
        disabledDate={disabledDate}
        allowClear={allowClear}
        className={className}
        style={style}
      />
    );
  }

  const emitChange = (nextValue) => {
    if (!controlled) setInnerValue(nextValue);
    onChange?.(nextValue, nextValue ? nextValue.map((item) => item.format(format)) : ["", ""]);
  };

  const clearValue = (event) => {
    event.stopPropagation();
    emitChange(null);
  };

  return (
    <>
      <div
        className={`responsive-picker-trigger${disabled ? " responsive-picker-trigger-disabled" : ""} ${className || ""}`}
        style={style}
        onClick={() => !disabled && setOpen(true)}
      >
        <span className={`responsive-picker-trigger-value${displayValue ? "" : " responsive-picker-trigger-placeholder"}`}>
          {displayValue || placeholderText}
        </span>
        <span className="responsive-picker-trigger-icons">
          {allowClear && displayValue && !disabled ? <CloseCircleFilled className="responsive-picker-clear" onClick={clearValue} /> : null}
          <CalendarOutlined />
        </span>
      </div>
      <Drawer
        open={open}
        title="Select date range"
        placement="bottom"
        height="72vh"
        className="responsive-picker-drawer"
        onClose={() => setOpen(false)}
        destroyOnClose
      >
        <div className="responsive-picker-body">
          <div className="responsive-date-tabs">
            <div className={`responsive-date-tab${active === "start" ? " responsive-date-tab-active" : ""}`} onClick={() => setActive("start")}>
              {moment(draftStart).format(format)}
            </div>
            <div className={`responsive-date-tab${active === "end" ? " responsive-date-tab-active" : ""}`} onClick={() => setActive("end")}>
              {moment(draftEnd).format(format)}
            </div>
          </div>
          <DatePickerView
            className="responsive-date-view"
            value={active === "start" ? draftStart : draftEnd}
            precision={getPickerMode(picker)}
            onChange={active === "start" ? setDraftStart : setDraftEnd}
          />
        </div>
        <div className="responsive-picker-footer">
          {allowClear ? <Button onClick={() => { emitChange(null); setOpen(false); }}>Clear</Button> : <Button onClick={() => setOpen(false)}>Cancel</Button>}
          <Button
            type="primary"
            onClick={() => {
              let start = toMoment(draftStart, picker);
              let end = toMoment(draftEnd, picker);
              if (start.isAfter(end)) [start, end] = [end, start];
              if (disabledDate?.(start) || disabledDate?.(end)) return;
              emitChange([start, end]);
              setOpen(false);
            }}
          >
            Confirm
          </Button>
        </div>
      </Drawer>
    </>
  );
};

const ResponsiveDatePicker = ResponsiveDatePickerBase;
ResponsiveDatePicker.RangePicker = ResponsiveRangePicker;
ResponsiveDatePicker.MonthPicker = AntdDatePicker.MonthPicker;
ResponsiveDatePicker.WeekPicker = AntdDatePicker.WeekPicker;
ResponsiveDatePicker.YearPicker = AntdDatePicker.YearPicker;

export default ResponsiveDatePicker;
